import axios from "axios";

import { IAsset, IAssetWithSignedUrls, IAssetsModel } from "../../types";

export interface ICreateAssetsParameters {
    files: File[];
    productId: string;
    variantId: string;
    token: string;
    tags?: string;
    position: number;
    header?: string;
    description?: string;
    altText?: string;
}

export interface IUpdateAssetsParameters {
    position: number;
    header?: string;
    description?: string;
    altText?: string;
}

export class Asset {
    private static transformAsset(data: any): IAssetsModel {
        return {
            ...data,
            createdAt: new Date(data.createdAt),
            updatedAt: new Date(data.updatedAt),
        };
    }

    public static async create(
        parameters: ICreateAssetsParameters,
    ): Promise<IAssetWithSignedUrls[]> {
        const {
            token,
            files,
            variantId,
            productId,
            tags,
            position,
            header,
            description,
            altText,
        } = parameters;

        const assets: IAsset[] = files.map((file) => ({
            fileName: file.name,
            fileType: file.type,
        }));

        const { data: assetData } = await axios.post(
            `${process.env.REACT_APP_API_BASE_URL}/api/admins/v1/assets`,
            {
                assets,
                variantId,
                productId,
                tags,
                position,
                header,
                description,
                altText,
            },
            {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            },
        );

        const assetsWithSignedUrls: IAssetWithSignedUrls[] = assetData;

        const uploadRequests = assetsWithSignedUrls.map((asset, index) => {
            const file = files[index];
            if (asset.signedUrl && file) {
                return axios.put(asset.signedUrl, file, {
                    headers: {
                        "Content-Type": file.type,
                    },
                });
            }
        });

        await Promise.all(uploadRequests);

        return assetsWithSignedUrls;
    }

    public static async getById(
        assetId: string,
        token: string,
    ): Promise<IAssetsModel> {
        const { data } = await axios.get(
            `${process.env.REACT_APP_API_BASE_URL}/api/admins/v1/assets/${assetId}`,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            },
        );

        return this.transformAsset(data);
    }

    public static async updateById(
        assetId: string,
        token: string,
        parameters: IUpdateAssetsParameters,
    ): Promise<IAssetsModel> {
        const { position, header, description, altText } = parameters;
        const { data } = await axios.patch(
            `${process.env.REACT_APP_API_BASE_URL}/api/admins/v1/assets/${assetId}`,
            { position, header, description, altText },
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            },
        );

        return this.transformAsset(data);
    }

    public static async deleteById(
        assetId: string,
        token: string,
    ): Promise<IAssetsModel> {
        const { data } = await axios.delete(
            `${process.env.REACT_APP_API_BASE_URL}/api/admins/v1/assets/${assetId}`,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            },
        );

        return this.transformAsset(data);
    }

    public static async createS3Assets(
        assets: IAssetWithSignedUrls[],
        files: File[],
    ): Promise<void> {
        if (assets.length === 0 || files.length === 0) return;

        const signedUrlByFile = new Map<string, File>();

        for (const [index, asset] of assets.entries()) {
            const file = files[index];
            if (asset.signedUrl && file) {
                signedUrlByFile.set(asset.signedUrl, file);
            }
        }

        const uploadRequests = Array.from(signedUrlByFile.entries()).map(
            async ([signedUrl, file]) => {
                await axios.put(signedUrl, file, {
                    headers: {
                        "Content-Type": file.type,
                    },
                });
            },
        );

        await Promise.all(uploadRequests);
    }
}
